// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Currency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { Project, ProjectAssetAssignment } from '@/project/api/types';
import { getRequestedAssetCount } from '@/project/utilities/get-requested-asset-count';
import { pluralize } from '@/utilities/pluralize';
import { Decimal } from 'decimal.js';
import { groupBy, sumBy } from 'lodash-es';

export const OTHER_PROJECT_DATA_ID = 'other';

function aggregateAssignments(
  projects: Pick<Project, 'assignments'>[]
): ProjectAssetAssignment[] {
  const assignments = projects.flatMap((project) => project.assignments);
  const grouped = groupBy(assignments, (assignment) => assignment.id);

  return Array.from(Object.keys(grouped)).map((itemId) => {
    const itemAssignments = grouped[itemId];
    const first = itemAssignments[0];

    return {
      ...first,
      assignedCount: sumBy(itemAssignments, 'assignedCount'),
      requestedCount: sumBy(itemAssignments, 'requestedCount'),
    };
  });
}

/** Sums amounts per ISO code, largest total first. */
export function sumByCurrency(values: Currency[]): Currency[] {
  const totals = new Map<IsoCurrencyCode, Decimal>();
  for (const value of values) {
    const total = totals.get(value.isoCode) ?? new CurrencyDecimal(0);
    totals.set(value.isoCode, total.plus(value.amount));
  }

  // The ISO-code tiebreak keeps the order deterministic for equal totals.
  return Array.from(totals, ([isoCode, amount]) => ({ isoCode, amount })).sort(
    (left, right) => {
      const byTotal = right.amount.comparedTo(left.amount);
      if (byTotal !== 0) {
        return byTotal;
      }

      return left.isoCode.localeCompare(right.isoCode);
    }
  );
}

function toContractValues(project: ProjectDatum): Currency[] {
  return project.totalContractValue === null
    ? []
    : [project.totalContractValue];
}

// Slice sizes are plotted as numbers; displayed totals stay per currency.
function toSliceSize(contractValues: Currency[]): number {
  return contractValues.reduce(
    (sum, contractValue) => sum + contractValue.amount.toNumber(),
    0
  );
}

function toProjectChartDatum(project: ProjectDatum) {
  const contractValues = toContractValues(project);

  return {
    id: project.id,
    label: project.name,
    assignments: project.assignments,
    requestedCount: getRequestedAssetCount(project),
    totalContractValue: toSliceSize(contractValues),
    totalContractValueByCurrency: contractValues,
  };
}

type ProjectDatum = Pick<
  Project,
  'id' | 'name' | 'assignments' | 'totalContractValue'
>;

type ProjectMetric = 'requestedCount' | 'totalContractValue';

interface Options {
  count: number;
  metric: ProjectMetric;
}

export function getProjectChartData(
  projects: ProjectDatum[],
  options: Options
) {
  const sortedProjects = projects.slice().sort((project, other) => {
    const projectCount = getRequestedAssetCount(project);
    const otherCount = getRequestedAssetCount(other);
    const projectTotalContractValue =
      project.totalContractValue?.amount ?? new CurrencyDecimal(0);
    const otherTotalContractValue =
      other.totalContractValue?.amount ?? new CurrencyDecimal(0);

    switch (options.metric) {
      case 'requestedCount':
        return otherCount - projectCount;

      case 'totalContractValue':
        return otherTotalContractValue.comparedTo(projectTotalContractValue);

      default:
        return options.metric satisfies never;
    }
  });

  const firstProjects = sortedProjects.slice(0, options.count);
  const otherProjects = sortedProjects.slice(options.count);

  if (otherProjects.length === 0) {
    return firstProjects.map(toProjectChartDatum);
  }

  const otherContractValues = sumByCurrency(
    otherProjects.flatMap(toContractValues)
  );

  return [
    ...firstProjects.map(toProjectChartDatum),
    {
      id: OTHER_PROJECT_DATA_ID,
      label: `${otherProjects.length.toLocaleString()} other ${pluralize(otherProjects.length, { singular: 'project', includeCount: false })}`,
      assignments: aggregateAssignments(otherProjects),
      requestedCount: sumBy(otherProjects, getRequestedAssetCount),
      totalContractValue: toSliceSize(otherContractValues),
      totalContractValueByCurrency: otherContractValues,
    },
  ];
}
